/**
 * The audit page's rows, folded: the same thing done again and again (a host pushing a repository's
 * memory in 24 batches, a check every minute) is one row with a count and a span, not a page of
 * identical lines. Rows fold while they are consecutive and alike: the same action, outcome, person
 * or agent, machine, project and target. The newest row speaks for the group; a memory push says the
 * totals of the whole run instead.
 */

import type { AuditEntry, AuditOrigin } from '@aioe/shared';

import { auditSentence } from './events.ts';

/** A row as the audit search answers it; `origin`: where and how it was done, when kept. */
export type AuditRow = AuditEntry & { deviceId: string; workspaceId: string; projectId?: string; seq?: number; hash?: string; origin?: AuditOrigin };

export interface AuditGroup {
  /** The newest row: what the group says. */
  head: AuditRow;
  /** Every row, newest first. */
  rows: AuditRow[];
  /** The sentence for the group: the head's, or the run's totals. */
  sentence: string | undefined;
}

/** Who did it, for folding: the person behind it, else the agent or the kind. */
export function actorKey(a: AuditRow): string {
  return a.actor.principal ?? (a.actor.kind === 'user' ? (a.actor.id ?? 'user') : `${a.actor.kind}:${a.actor.id ?? ''}`);
}

function alike(a: AuditRow, b: AuditRow): boolean {
  return a.action === b.action && a.outcome === b.outcome && actorKey(a) === actorKey(b) && a.deviceId === b.deviceId && a.projectId === b.projectId && a.target === b.target && (a.error ?? '') === (b.error ?? '');
}

const n = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** A run of memory pushes in one sentence: the repositories, how many batches, and what moved in all. */
function ingestSentence(rows: AuditRow[]): string {
  const roots = [...new Set(rows.map((r) => (typeof r.detail?.rootId === 'string' ? r.detail.rootId : undefined)).filter((x): x is string => !!x))];
  const up = rows.reduce((s, r) => s + n(r.detail?.upserts), 0);
  const gone = rows.reduce((s, r) => s + n(r.detail?.removed), 0);
  const revisions = new Set(rows.map((r) => (typeof r.detail?.revision === 'string' ? r.detail.revision : '')).filter(Boolean));
  const what = [up ? `${up.toLocaleString()} ${up === 1 ? 'item' : 'items'} added or changed` : undefined, gone ? `${gone.toLocaleString()} removed` : undefined].filter(Boolean).join(', ');
  const where = roots.length === 1 ? `The memory of “${roots[0]}”` : roots.length ? `The memory of ${roots.length} repositories` : 'A repository’s memory';
  const times = revisions.size > 1 ? `, over ${revisions.size} revisions` : '';
  return `${where} was pushed in ${rows.length} batches${times}${what ? `: ${what}` : ''}.`;
}

export function foldAudit(list: AuditRow[]): AuditGroup[] {
  const out: AuditGroup[] = [];
  for (const row of list) {
    const last = out.at(-1);
    if (last && alike(last.head, row)) {
      last.rows.push(row);
      continue;
    }
    out.push({ head: row, rows: [row], sentence: undefined });
  }
  for (const g of out) g.sentence = g.rows.length > 1 && g.head.action === 'memory.ingest' ? ingestSentence(g.rows) : auditSentence(g.head);
  return out;
}
